import {
  AiAgent,
  ContextDocument,
  FileMimeType,
  FileUploadCategoryConfig,
  FileUploadConfig,
  UploadContextFile,
} from '../../../common/ai.dictionary'
import {
  ContextDocumentContent,
  ContextDocumentResponse,
  DocumentUploadResponse,
} from '../api/documents.api'

function extractContentFromDocumentResponse(content: ContextDocumentContent[]): {
  [key in AiAgent]?: ContextDocumentContent[]
} {
  if (!content) {
    return null
  }
  return content.reduce(
    (acc, item) => {
      const typeToAgentMap = {
        business_process: AiAgent.ANALYST,
        screen_name: AiAgent.DESIGNER,
        business_rule: AiAgent.DEVELOPER,
        screen_expression: AiAgent.DEVELOPER,
        compliance_check: AiAgent.AUDITOR,
      }

      return typeToAgentMap[item.type]
        ? { ...acc, [typeToAgentMap[item.type]]: [...acc[typeToAgentMap[item.type]], item] }
        : acc
    },
    {
      [AiAgent.ANALYST]: [],
      [AiAgent.DESIGNER]: [],
      [AiAgent.DEVELOPER]: [],
      [AiAgent.AUDITOR]: [],
    }
  )
}

export class AiDocumentsTransformService {
  static configResponseTransform(config: FileUploadConfig): FileUploadConfig {
    const mergeFileUploadConfigs = (
      configs: FileUploadCategoryConfig[]
    ): FileUploadCategoryConfig =>
      configs.reduce(
        (acc, config) => ({
          fileType: [...acc.fileType, ...config.fileType],
          maxFileSize: Math.min(acc.maxFileSize, config.maxFileSize),
          maxFileNumber: Math.min(acc.maxFileNumber, config.maxFileNumber),
        }),
        {
          fileType: [],
          maxFileSize: Infinity,
          maxFileNumber: Infinity,
        }
      )

    return {
      ...config,
      fileUpload: {
        [AiAgent.ANALYST]: mergeFileUploadConfigs([
          config.imageUpload,
          config.documentUpload,
          config.bpmnUpload,
        ]),
        [AiAgent.DEVELOPER]: config.documentUpload,
        default: mergeFileUploadConfigs([config.imageUpload, config.documentUpload]),
      },
    }
  }

  static contextDocumentResponseTransform(response: ContextDocumentResponse): ContextDocument[] {
    return response.map((document) => ({
      id: document.id,
      resourceId: document.resource_id,
      type: document.file_properties.file_type,
      file_name: document.file_name,
      name: document.name,
      documentId: document.db_collection,
      description: document.document_desc || 'No description available',
      documentType: document.document_type,
      summary: document.details?.summary || 'No summary available',
      created_at: document.updated_at || document.created_at,
      images: document.details?.images,
      status: document.status,
      storagePath: document.file_properties?.object_name,
      ...extractContentFromDocumentResponse(document.details?.content),
    }))
  }

  static uploadDocumentResponseTransform(response: DocumentUploadResponse): ContextDocument[] {
    return response.files_status.map((file) => ({
      id: file.unique_id,
      type: file.fileName.endsWith('.pdf') ? FileMimeType.PDF : null,
      name: file.fileName,
      documentId: file.unique_id,
      summary: file.response.summary,
      description: file.response.document_desc,
      documentType: file.response.document_type,
      images: file.response.images,
      status: file.status,
      ...extractContentFromDocumentResponse(file.response.content),
    }))
  }

  static mapDocumentsToMediaLibraryUploadPayload(document: UploadContextFile): {
    formData: FormData
    key: string
    tags: string[]
  } {
    const formData = new FormData()

    formData.append('file', document.file)

    return {
      formData,
      key: document.id,
      tags: ['ai-files', 'parent'],
    }
  }
}
